import React, { useEffect, useId, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  buildAddAdminProspectNoteRequest,
  buildPatchAdminProspectFollowUpRequest,
} from '../../services/admin/prospectsListRequest';
import { addAdminProspectNote, updateAdminProspect } from '../../services/admin/prospectsService';
import {
  ADMIN_DATE_PLACEHOLDER,
  adminProspectListErrorKey,
  adminProspectNoteErrorKey,
  adminProspectStatusLabel,
  adminProspectTelHref,
  classifyAdminCallableError,
  displayAdminProspectEmail,
  displayAdminProspectPhone,
  formatAdminDate,
  isFollowUpOverdue,
  isoToDatetimeLocal,
} from './adminProspectDisplay';
import {
  adminProspectFollowUpTimeZoneLabel,
  adminProspectQuickFollowUpStatusOptions,
  parseAdminProspectDatetimeLocal,
} from './adminProspectQuickFollowUp';
import {
  buildFollowUpPatchFields,
  captureQuickFollowUpSaveRequest,
  beginQuickFollowUpNoteWrite,
  clearQuickFollowUpNoteHistoryRefreshFailure,
  clearQuickFollowUpRefreshFailure,
  createQuickFollowUpFormState,
  dismissQuickFollowUpReview,
  enterQuickFollowUpConflictReview,
  failQuickFollowUpSave,
  focusProspectPublicResponseSection,
  markQuickFollowUpNoteHistoryRefreshFailure,
  markQuickFollowUpNoteWriteError,
  markQuickFollowUpNoteWriteSuccess,
  markQuickFollowUpRefreshFailure,
  reconcileQuickFollowUpControl,
  runQuickFollowUpControlSave,
  setQuickFollowUpDraft,
  shouldApplyQuickFollowUpLiveResult,
  syncQuickFollowUpFormFromProspect,
} from './adminProspectQuickFollowUpController';

function formatReviewValue(control, value, t, language) {
  if (control === 'status') return adminProspectStatusLabel(value, t);
  if (value == null || value === '') return ADMIN_DATE_PLACEHOLDER;
  return formatAdminDate(value, language);
}

function noteErrorUncertainDuplicate(kind) {
  return kind === 'unavailable' || kind === 'internal' || kind === 'unknown';
}

export default function AdminProspectQuickFollowUpPanel({
  prospect,
  prospectId,
  visitId,
  language,
  t,
  onFollowUpSaved,
  onNoteSaved,
  activeProspectId,
  hideTitle = false,
  onEnsurePublicResponsesExpanded,
  onSectionAttention,
}) {
  const location = useLocation();
  const titleId = useId();
  const noteId = useId();
  const statusId = useId();
  const followUpId = useId();
  const lastContactId = useId();
  const mountedRef = useRef(true);
  const activeProspectIdRef = useRef(String(activeProspectId || prospectId || prospect?.id || '').trim());
  const activeVisitIdRef = useRef(visitId);
  const [formState, setFormState] = useState(() => createQuickFollowUpFormState(prospect));

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    activeProspectIdRef.current = String(activeProspectId || prospectId || prospect?.id || '').trim();
    activeVisitIdRef.current = visitId;
  }, [activeProspectId, prospectId, prospect?.id, visitId]);

  useEffect(() => {
    setFormState((current) => syncQuickFollowUpFormFromProspect(current, prospect));
  }, [
    prospect?.id,
    prospect?.status,
    prospect?.nextFollowUpAt,
    prospect?.lastContactAt,
    prospect?.updatedAt,
    prospect?.updatedAtVersion,
  ]);

  if (!prospect) return null;

  const ownerProspectId = String(prospectId || prospect.id || '').trim();

  const notifyAttention = (reason) => {
    if (typeof onSectionAttention !== 'function') return;
    onSectionAttention({
      sectionId: 'followUp',
      ownerProspectId,
      visitId,
      reason,
    });
  };

  const mayApplyLive = (requestVisitId, requestOwnerId = ownerProspectId) =>
    shouldApplyQuickFollowUpLiveResult({
      ownerProspectId: requestOwnerId,
      requestVisitId,
      getActiveProspectId: () => activeProspectIdRef.current,
      getActiveVisitId: () => activeVisitIdRef.current,
      getMounted: () => mountedRef.current,
    });

  const anyCrmBusy = ['status', 'nextFollowUpAt', 'lastContactAt'].some(
    (control) => formState.controlUi[control].saving,
  );

  const statusOptions = adminProspectQuickFollowUpStatusOptions(prospect.status);
  const overdue = isFollowUpOverdue(prospect.nextFollowUpAt);
  const timeZone = adminProspectFollowUpTimeZoneLabel();
  const email = displayAdminProspectEmail(prospect.primaryContact?.email);
  const phone = displayAdminProspectPhone(prospect.primaryContact?.phone);
  const telHref = adminProspectTelHref(phone);

  const applyRefreshResult = (control, refresh, requestVisitId) => {
    if (!mayApplyLive(requestVisitId)) return;
    if (refresh?.prospect) {
      setFormState((current) => syncQuickFollowUpFormFromProspect(current, refresh.prospect));
    }
    if (refresh?.refreshOk === false && refresh?.applied) {
      setFormState((current) => markQuickFollowUpRefreshFailure(current, control));
      notifyAttention('refresh-failure');
    } else if (refresh?.refreshOk) {
      setFormState((current) => clearQuickFollowUpRefreshFailure(current));
    }
  };

  const handleViewResponses = () => {
    const result = focusProspectPublicResponseSection({
      documentObj: typeof document !== 'undefined' ? document : null,
      locationObj: location,
      ensureExpanded:
        typeof onEnsurePublicResponsesExpanded === 'function'
          ? onEnsurePublicResponsesExpanded
          : null,
    });
    setFormState((current) => ({
      ...current,
      responseJumpMessageKey: result.ok
        ? null
        : 'admin.prospection.quickFollowUp.viewResponses.unavailable',
    }));
  };

  const handleAddNote = async (event) => {
    event.preventDefault();
    const body = formState.note.body.trim();
    if (!body || formState.note.status === 'pending') {
      if (!body) {
        setFormState((current) => ({
          ...current,
          note: { ...current.note, errorKey: 'admin.prospection.note.error.empty' },
        }));
        notifyAttention('note-validation');
      }
      return;
    }
    const requestVisitId = visitId;
    let noteRevision = 0;
    setFormState((current) => {
      const next = beginQuickFollowUpNoteWrite(current);
      noteRevision = next.note.revision;
      return next;
    });
    try {
      await addAdminProspectNote(buildAddAdminProspectNoteRequest({ prospectId: ownerProspectId, body }));
      if (!mayApplyLive(requestVisitId)) return;
      setFormState((current) => markQuickFollowUpNoteWriteSuccess(current, { noteRevision }));
      const refresh = await onNoteSaved?.({ prospectId: ownerProspectId, visitId: requestVisitId });
      if (!mayApplyLive(requestVisitId)) return;
      if (refresh && refresh.applied && refresh.timelineOk === false) {
        setFormState((current) =>
          markQuickFollowUpNoteHistoryRefreshFailure(current, { noteRevision }),
        );
      } else if (refresh?.timelineOk) {
        setFormState((current) =>
          clearQuickFollowUpNoteHistoryRefreshFailure(current, { noteRevision }),
        );
      }
    } catch (err) {
      if (!mayApplyLive(requestVisitId)) return;
      const kind = classifyAdminCallableError(err);
      setFormState((current) =>
        markQuickFollowUpNoteWriteError(current, {
          noteRevision,
          errorKey: adminProspectNoteErrorKey(kind),
          uncertainDuplicateRisk: noteErrorUncertainDuplicate(kind),
        }),
      );
      notifyAttention('note-error');
    }
  };

  const handleNoteHistoryRefreshRetry = async () => {
    if (!formState.note.historyRefreshFailed) return;
    const requestVisitId = visitId;
    const noteRevision = formState.note.revision;
    const refresh = await onNoteSaved?.({ prospectId: ownerProspectId, visitId: requestVisitId });
    if (!mayApplyLive(requestVisitId)) return;
    if (refresh?.timelineOk) {
      setFormState((current) =>
        clearQuickFollowUpNoteHistoryRefreshFailure(current, { noteRevision }),
      );
    } else if (refresh && refresh.applied && refresh.timelineOk === false) {
      setFormState((current) =>
        markQuickFollowUpNoteHistoryRefreshFailure(current, { noteRevision }),
      );
    }
  };

  const saveControl = async (control, event, { fieldsOverride } = {}) => {
    event?.preventDefault();
    if (formState.controlUi[control].saving || anyCrmBusy) return;
    if (formState.review[control]?.conflict) return;

    if (control === 'status') {
      const next = formState.drafts.status;
      if (next === prospect.status) {
        setFormState((current) =>
          reconcileQuickFollowUpControl(current, 'status', next, current.baselines.versionToken),
        );
        return;
      }
      if (next === 'converted' && prospect.status !== 'converted') {
        setFormState((current) =>
          failQuickFollowUpSave(current, 'status', {
            errorKey: 'admin.prospection.quickFollowUp.status.convertedBlocked',
          }),
        );
        notifyAttention('status-validation');
        return;
      }
    }

    const built = fieldsOverride
      ? { ok: true, fields: fieldsOverride }
      : buildFollowUpPatchFields(control, formState);
    if (!built.ok) {
      setFormState((current) =>
        failQuickFollowUpSave(current, control, { errorKey: built.errorKey }),
      );
      notifyAttention('datetime-validation');
      return;
    }

    if (control === 'nextFollowUpAt' && !fieldsOverride) {
      const parsed = parseAdminProspectDatetimeLocal(formState.drafts.nextFollowUpAtLocal);
      if (parsed.empty) {
        if (!prospect.nextFollowUpAt && !formState.drafts.nextFollowUpAtLocal.trim()) {
          setFormState((current) =>
            reconcileQuickFollowUpControl(current, 'nextFollowUpAt', null, current.baselines.versionToken),
          );
          return;
        }
        if (prospect.nextFollowUpAt) {
          setFormState((current) =>
            failQuickFollowUpSave(current, 'nextFollowUpAt', {
              errorKey: 'admin.prospection.quickFollowUp.followUp.clearRequired',
            }),
          );
          notifyAttention('follow-up-validation');
          return;
        }
      }
      if (parsed.ok && parsed.iso === (prospect.nextFollowUpAt || null)) {
        setFormState((current) =>
          reconcileQuickFollowUpControl(current, 'nextFollowUpAt', parsed.iso, current.baselines.versionToken),
        );
        return;
      }
    }

    if (control === 'lastContactAt' && !fieldsOverride) {
      const parsed = parseAdminProspectDatetimeLocal(formState.drafts.lastContactAtLocal);
      if (parsed.ok && (parsed.iso || null) === (prospect.lastContactAt || null)) {
        setFormState((current) =>
          reconcileQuickFollowUpControl(current, 'lastContactAt', parsed.iso, current.baselines.versionToken),
        );
        return;
      }
    }

    if (!fieldsOverride && !formState.dirty[control]) {
      const savedValue =
        control === 'status'
          ? formState.drafts.status
          : control === 'nextFollowUpAt'
            ? prospect.nextFollowUpAt
            : prospect.lastContactAt;
      setFormState((current) =>
        reconcileQuickFollowUpControl(current, control, savedValue, current.baselines.versionToken),
      );
      return;
    }

    const requestVisitId = visitId;
    const savedValue =
      control === 'status'
        ? built.fields.status
        : control === 'nextFollowUpAt'
          ? built.fields.nextFollowUpAt
          : built.fields.lastContactAt;
    const request = captureQuickFollowUpSaveRequest(formState, control, {
      fields: built.fields,
      savedValue,
    });

    const saveResult = await runQuickFollowUpControlSave({
      applyState: (updater) => setFormState(updater),
      mayApply: mayApplyLive,
      request,
      requestVisitId,
      classifyError: classifyAdminCallableError,
      write: async ({ fields, versionToken }) => {
        await updateAdminProspect(
          buildPatchAdminProspectFollowUpRequest(ownerProspectId, fields, versionToken),
        );
      },
      onConflict: async () => {
        setFormState((current) => enterQuickFollowUpConflictReview(current, control, prospect));
        notifyAttention('conflict');
        const refresh = await onFollowUpSaved?.({
          prospectId: ownerProspectId,
          visitId: requestVisitId,
          conflictReload: true,
        });
        if (refresh?.prospect && mayApplyLive(requestVisitId)) {
          setFormState((current) =>
            enterQuickFollowUpConflictReview(
              syncQuickFollowUpFormFromProspect(current, refresh.prospect),
              control,
              refresh.prospect,
            ),
          );
        }
      },
      onError: async (_err, kind) => {
        setFormState((current) =>
          failQuickFollowUpSave(current, control, {
            errorKey: adminProspectListErrorKey(kind),
            errorKind: kind,
          }),
        );
        notifyAttention('save-error');
      },
    });

    if (!saveResult.ok || saveResult.stale || saveResult.conflict) return;

    const refresh = await onFollowUpSaved?.({
      prospectId: ownerProspectId,
      visitId: requestVisitId,
      control,
    });
    applyRefreshResult(control, refresh, requestVisitId);
  };

  const handleClearFollowUp = async () => {
    if (formState.controlUi.nextFollowUpAt.saving || anyCrmBusy) return;
    if (!prospect.nextFollowUpAt && !formState.drafts.nextFollowUpAtLocal.trim()) {
      setFormState((current) =>
        reconcileQuickFollowUpControl(current, 'nextFollowUpAt', null, current.baselines.versionToken),
      );
      return;
    }
    await saveControl('nextFollowUpAt', null, { fieldsOverride: { nextFollowUpAt: null } });
  };

  const handleConflictKeepDraft = (control) => {
    setFormState((current) => dismissQuickFollowUpReview(current, control, { keepDraft: true }));
  };

  const handleConflictUseServer = (control) => {
    setFormState((current) => dismissQuickFollowUpReview(current, control, { keepDraft: false }));
  };

  const handleConflictReload = async (control) => {
    const requestVisitId = visitId;
    const refresh = await onFollowUpSaved?.({
      prospectId: ownerProspectId,
      visitId: requestVisitId,
      conflictReload: true,
    });
    if (refresh?.prospect && mayApplyLive(requestVisitId)) {
      setFormState((current) =>
        enterQuickFollowUpConflictReview(
          syncQuickFollowUpFormFromProspect(current, refresh.prospect),
          control,
          refresh.prospect,
        ),
      );
    }
  };

  const handleRefreshRetry = async () => {
    const control = formState.refreshAfterWrite?.control;
    if (!control) return;
    const requestVisitId = visitId;
    // Do not clear the warning before retry — only clear after required reads succeed.
    const refresh = await onFollowUpSaved?.({
      prospectId: ownerProspectId,
      visitId: requestVisitId,
      refreshOnly: true,
    });
    applyRefreshResult(control, refresh, requestVisitId);
  };

  const renderConflictReview = (control) => {
    const review = formState.review[control];
    if (!review?.conflict) return null;
    return (
      <div
        className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950"
        role="alert"
        data-testid={`quick-follow-up-conflict-${control}`}
      >
        <p className="font-semibold">{t('admin.prospection.quickFollowUp.conflict.title')}</p>
        <p className="mt-1">{t('admin.prospection.quickFollowUp.conflict.message')}</p>
        <dl className="mt-2 space-y-1 text-xs">
          <div>
            <dt className="font-medium">{t('admin.prospection.quickFollowUp.conflict.serverValue')}</dt>
            <dd>{formatReviewValue(control, review.serverValue, t, language)}</dd>
          </div>
          <div>
            <dt className="font-medium">{t('admin.prospection.quickFollowUp.conflict.pendingValue')}</dt>
            <dd>{formatReviewValue(control, review.draftValue, t, language)}</dd>
          </div>
        </dl>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => handleConflictKeepDraft(control)}
            className="min-h-9 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm font-semibold text-amber-950 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
          >
            {t('admin.prospection.quickFollowUp.conflict.keepDraft')}
          </button>
          <button
            type="button"
            onClick={() => handleConflictUseServer(control)}
            className="min-h-9 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm font-semibold text-amber-950 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
          >
            {t('admin.prospection.quickFollowUp.conflict.useServer')}
          </button>
          <button
            type="button"
            onClick={() => handleConflictReload(control)}
            className="min-h-9 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm font-semibold text-amber-950 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
          >
            {t('admin.prospection.quickFollowUp.conflict.reload')}
          </button>
        </div>
      </div>
    );
  };

  const renderControlFeedback = (control, successKey) => {
    const ui = formState.controlUi[control];
    if (ui.errorKey) {
      return (
        <p className="text-sm text-red-700" role="alert">
          {t(ui.errorKey)}
        </p>
      );
    }
    if (ui.success) {
      return (
        <p className="text-sm text-emerald-800" role="status">
          {t(successKey)}
        </p>
      );
    }
    return null;
  };

  return (
    <section
      className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-4"
      aria-labelledby={titleId}
      data-testid="admin-prospect-quick-follow-up"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          {!hideTitle ? (
            <h2 id={titleId} className="text-sm font-semibold text-slate-900">
              {t('admin.prospection.quickFollowUp.title')}
            </h2>
          ) : (
            <span id={titleId} className="sr-only">
              {t('admin.prospection.quickFollowUp.title')}
            </span>
          )}
          <p className={`${hideTitle ? 'mt-0' : 'mt-1'} text-sm text-slate-600`}>
            {t('admin.prospection.quickFollowUp.subtitle')}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {phone && telHref ? (
            <a
              href={telHref}
              className="inline-flex min-h-10 items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              aria-label={t('admin.prospection.primaryContact.phoneAction').replace('{phone}', phone)}
            >
              {t('admin.prospection.quickFollowUp.call')}
            </a>
          ) : null}
          {email ? (
            <a
              href={`mailto:${email}`}
              className="inline-flex min-h-10 items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              aria-label={t('admin.prospection.primaryContact.emailAction').replace('{email}', email)}
            >
              {t('admin.prospection.quickFollowUp.email')}
            </a>
          ) : null}
          <button
            type="button"
            onClick={handleViewResponses}
            className="inline-flex min-h-10 items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {t('admin.prospection.quickFollowUp.viewResponses')}
          </button>
        </div>
      </div>

      {formState.responseJumpMessageKey ? (
        <p className="mt-3 text-sm text-amber-800" role="status">
          {t(formState.responseJumpMessageKey)}
        </p>
      ) : null}

      {formState.refreshAfterWrite ? (
        <div
          className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950"
          role="alert"
          data-testid="quick-follow-up-refresh-failure"
        >
          <p>{t(formState.refreshAfterWrite.messageKey)}</p>
          <button
            type="button"
            onClick={handleRefreshRetry}
            className="mt-2 min-h-9 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm font-semibold text-amber-950 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
          >
            {t('admin.prospection.quickFollowUp.refreshRetry')}
          </button>
        </div>
      ) : null}

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <form onSubmit={handleAddNote} className="space-y-2 rounded-lg border border-slate-200 bg-white p-3">
          <label className="block text-sm font-medium text-slate-700" htmlFor={noteId}>
            {t('admin.prospection.quickFollowUp.note.label')}
            <span className="mt-0.5 block text-xs font-normal text-slate-500">
              {t('admin.prospection.quickFollowUp.note.hint')}
            </span>
            <textarea
              id={noteId}
              name="body"
              rows={3}
              value={formState.note.body}
              disabled={formState.note.status === 'pending'}
              onChange={(event) => {
                setFormState((current) => ({
                  ...current,
                  note: {
                    ...current.note,
                    body: event.target.value,
                    status: current.note.status === 'success' || current.note.status === 'error' ? 'idle' : current.note.status,
                    errorKey: null,
                  },
                }));
              }}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            />
          </label>
          {formState.note.errorKey ? (
            <p className="text-sm text-red-700" role="alert">
              {t(formState.note.errorKey)}
            </p>
          ) : null}
          {formState.note.uncertainDuplicateRisk ? (
            <p className="text-sm text-amber-800" role="status">
              {t('admin.prospection.quickFollowUp.note.duplicateWarning')}
            </p>
          ) : null}
          {formState.note.status === 'success' && !formState.note.historyRefreshFailed ? (
            <p className="text-sm text-emerald-800" role="status">
              {t('admin.prospection.quickFollowUp.note.success')}
            </p>
          ) : null}
          {formState.note.historyRefreshFailed ? (
            <div
              className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950"
              role="alert"
              data-testid="quick-follow-up-note-history-refresh-failure"
            >
              <p>{t('admin.prospection.quickFollowUp.note.historyRefreshFailed')}</p>
              <button
                type="button"
                onClick={handleNoteHistoryRefreshRetry}
                className="mt-2 min-h-9 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm font-semibold text-amber-950 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                {t('admin.prospection.quickFollowUp.note.historyRefreshRetry')}
              </button>
            </div>
          ) : null}
          <button
            type="submit"
            disabled={formState.note.status === 'pending' || !formState.note.body.trim()}
            className="min-h-10 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
          >
            {formState.note.status === 'pending'
              ? t('admin.prospection.note.saving')
              : t('admin.prospection.quickFollowUp.note.submit')}
          </button>
        </form>

        <form
          onSubmit={(event) => saveControl('status', event)}
          className="space-y-2 rounded-lg border border-slate-200 bg-white p-3"
        >
          <label className="block text-sm font-medium text-slate-700" htmlFor={statusId}>
            {t('admin.prospection.field.status')}
            <select
              id={statusId}
              name="status"
              value={formState.drafts.status}
              disabled={formState.controlUi.status.saving}
              onChange={(event) => {
                setFormState((current) => setQuickFollowUpDraft(current, 'status', event.target.value));
              }}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            >
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {adminProspectStatusLabel(status, t)}
                </option>
              ))}
            </select>
          </label>
          <p className="text-xs text-slate-500">
            {t('admin.prospection.quickFollowUp.status.current')}:{' '}
            {adminProspectStatusLabel(prospect.status, t)}
          </p>
          {renderConflictReview('status')}
          {renderControlFeedback('status', 'admin.prospection.quickFollowUp.status.success')}
          <button
            type="submit"
            disabled={formState.controlUi.status.saving || anyCrmBusy}
            className="min-h-10 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
          >
            {formState.controlUi.status.saving
              ? t('admin.prospection.quickFollowUp.saving')
              : t('admin.prospection.quickFollowUp.status.save')}
          </button>
        </form>

        <form
          onSubmit={(event) => saveControl('nextFollowUpAt', event)}
          className="space-y-2 rounded-lg border border-slate-200 bg-white p-3"
        >
          <label className="block text-sm font-medium text-slate-700" htmlFor={followUpId}>
            {t('admin.prospection.field.nextFollowUpAt')}
            <input
              id={followUpId}
              name="nextFollowUpAt"
              type="datetime-local"
              value={formState.drafts.nextFollowUpAtLocal}
              disabled={formState.controlUi.nextFollowUpAt.saving}
              onChange={(event) => {
                setFormState((current) => setQuickFollowUpDraft(current, 'nextFollowUpAt', event.target.value));
              }}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            />
          </label>
          <p className="text-xs text-slate-500">
            {t('admin.prospection.quickFollowUp.datetime.hint')}
            {timeZone ? ` (${timeZone})` : ''}
          </p>
          <p className="text-sm text-slate-700">
            {t('admin.prospection.quickFollowUp.followUp.scheduled')}:{' '}
            <span className={overdue ? 'font-semibold text-amber-900' : undefined}>
              {formatAdminDate(prospect.nextFollowUpAt, language)}
            </span>
            {overdue ? (
              <span className="ml-2 text-xs font-semibold text-amber-800">
                {t('admin.prospection.followUp.overdue')}
              </span>
            ) : null}
            {!prospect.nextFollowUpAt ? (
              <span className="text-slate-500"> {ADMIN_DATE_PLACEHOLDER}</span>
            ) : null}
          </p>
          {renderConflictReview('nextFollowUpAt')}
          {renderControlFeedback('nextFollowUpAt', 'admin.prospection.quickFollowUp.followUp.success')}
          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              disabled={formState.controlUi.nextFollowUpAt.saving || anyCrmBusy}
              className="min-h-10 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            >
              {formState.controlUi.nextFollowUpAt.saving
                ? t('admin.prospection.quickFollowUp.saving')
                : t('admin.prospection.quickFollowUp.followUp.save')}
            </button>
            <button
              type="button"
              disabled={formState.controlUi.nextFollowUpAt.saving || anyCrmBusy}
              onClick={handleClearFollowUp}
              className="min-h-10 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            >
              {t('admin.prospection.quickFollowUp.followUp.clear')}
            </button>
          </div>
        </form>

        <form
          onSubmit={(event) => saveControl('lastContactAt', event)}
          className="space-y-2 rounded-lg border border-slate-200 bg-white p-3"
        >
          <label className="block text-sm font-medium text-slate-700" htmlFor={lastContactId}>
            {t('admin.prospection.field.lastContactAt')}
            <input
              id={lastContactId}
              name="lastContactAt"
              type="datetime-local"
              value={formState.drafts.lastContactAtLocal}
              disabled={formState.controlUi.lastContactAt.saving}
              onChange={(event) => {
                setFormState((current) => setQuickFollowUpDraft(current, 'lastContactAt', event.target.value));
              }}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            />
          </label>
          <p className="text-xs text-slate-500">{t('admin.prospection.quickFollowUp.lastContact.hint')}</p>
          {renderConflictReview('lastContactAt')}
          {renderControlFeedback('lastContactAt', 'admin.prospection.quickFollowUp.lastContact.success')}
          <button
            type="submit"
            disabled={formState.controlUi.lastContactAt.saving || anyCrmBusy}
            className="min-h-10 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
          >
            {formState.controlUi.lastContactAt.saving
              ? t('admin.prospection.quickFollowUp.saving')
              : t('admin.prospection.quickFollowUp.lastContact.save')}
          </button>
        </form>
      </div>
    </section>
  );
}
